import { useCallback, useEffect, useState } from "react";

import { useStorage } from "@/common/hooks/storage.hooks";

/** Same key and shape (`string[]` of terms) as the previous search box, so history carries over. */
const STORAGE_KEY = "search_history";

const MAX_RECENT = 6;

/**
 * The user's own recent searches, kept on the device.
 *
 * Typesense analytics is aggregate-only (`{ q, count }`), so there is no per-user history to read
 * back from the search service — and keeping it local means it needs no auth, works for anonymous
 * visitors, and never leaves the browser.
 */
export const useRecentSearches = () => {
  const [terms, setTerms] = useState<string[]>([]);

  const { load, save } = useStorage("local", detail => {
    /* Desktop and mobile headers are both mounted, so two instances of this hook exist. `save`
     * broadcasts, which keeps whichever one the user is not looking at in sync. */
    if (detail.key === STORAGE_KEY) {
      setTerms(detail.data as string[]);
    }
  });

  /* localStorage is unavailable during SSR — read it once mounted. */
  useEffect(() => {
    setTerms(load<string[]>(STORAGE_KEY) ?? []);
  }, [load]);

  const add = useCallback(
    (term: string) => {
      const normalized = term.trim();

      if (!normalized) {
        return;
      }

      /* Read through to storage rather than state: `add` is called from a submit handler that may
       * not have re-rendered since the last write, and the other mounted instance may own it. */
      const previous = load<string[]>(STORAGE_KEY) ?? [];
      const withoutDuplicate = previous.filter(
        entry => entry.toLowerCase() !== normalized.toLowerCase()
      );

      save(STORAGE_KEY, [normalized, ...withoutDuplicate].slice(0, MAX_RECENT));
    },
    [load, save]
  );

  return { recentTerms: terms, addRecentTerm: add };
};
